import { useCallback } from 'react';

import { useActorRef, useSelector } from '@xstate/react';

import { OrderPaymentStatus } from '@sg/graphql-schema';

import { paymentStatePollingMachine } from './payment-state-polling-machine';
import { PaymentStatePollingActions } from './payment-state-polling-machine.actions';
import { PaymentStatePollingActors } from './payment-state-polling-machine.actors';
import { PaymentState } from './payment-state-polling-machine.context';

// ─────────────────────────────────────────────────────────────────────────────

export const usePaymentStatePollingMachine = (params: UsePaymentStatePollingParams) => {
  const {
    paymentStatePollingInterval,
    paymentStateExpirationTolerance,
    paymentStateDelayedTolerance,
    fetchPaymentState,
    cancelPayment,
    clearPendingPayment,
    incrementPurchaseAttempt,
    onPaymentStateError,
    onPaymentCanceled,
    onPaymentFulfilled,
  } = params;

  // ─── Setup ─────────────────────────────────────────────────────────────────

  const actorRef = useActorRef(
    paymentStatePollingMachine.provide({
      actions: {
        clearPendingPayment,
        incrementPurchaseAttempt,
        onPaymentStateError,
        onPaymentCanceled,
        onPaymentFulfilled,
      },
      actors: {
        fetchPaymentState,
        cancelPayment,
      },
    }),
    {
      input: {
        pollingConfiguration: {
          paymentStatePollingInterval,
          paymentStateExpirationTolerance,
          paymentStateDelayedTolerance,
        },
      },
    },
  );

  const context = useSelector(actorRef, (snapshot) => snapshot.context);

  // ─── Actions ─────────────────────────────────────────────────────────

  const startPolling = useCallback(
    (orderId: string) => {
      actorRef.send({ type: 'START', params: { orderId } });
    },
    [actorRef],
  );

  const resetPolling = useCallback(() => {
    actorRef.send({ type: 'RESET' });
  }, [actorRef]);

  const createPayment = useCallback(
    (paymentState: PaymentState) => {
      actorRef.send({ type: 'CREATE_PAYMENT', params: paymentState });
    },
    [actorRef],
  );

  const clearPaymentState = useCallback(() => {
    actorRef.send({ type: 'CLEAR_PAYMENT_STATE' });
  }, [actorRef]);

  const failPayment = useCallback(() => {
    actorRef.send({ type: 'FAIL_PAYMENT' });
  }, [actorRef]);

  const requestPaymentCancelation = useCallback(() => {
    actorRef.send({ type: 'CANCEL_PAYMENT' });
  }, [actorRef]);

  // ─── Flags ───────────────────────────────────────────────────────────

  const hasPendingPayment = useSelector(actorRef, (state) => {
    return Boolean(state.context.paymentState?.paymentIntentId);
  });

  const isPollingPayment = useSelector(actorRef, (state) => {
    return (
      state.matches({ polling: 'waiting' }) ||
      state.matches({ polling: 'polling' }) ||
      state.matches({ polling: 'checking' })
    );
  });

  const isPaymentPending = hasPendingPayment && isPollingPayment;

  const isPaymentDelayed = useSelector(actorRef, (state) => {
    const { context: ctx } = state;

    const paymentState = ctx.paymentState;
    const canCancelPaymentIntent = Boolean(
      paymentState?.paymentIntentId &&
      paymentState?.paymentStateStatus === OrderPaymentStatus.Created,
    );

    return state.matches({ delayed: 'delayed' }) && canCancelPaymentIntent && isPollingPayment;
  });

  const isCancelingPayment = useSelector(actorRef, (state) => {
    return state.matches({ polling: 'canceling' });
  });

  const isPaymentFailed = useSelector(actorRef, (state) => {
    return state.matches({ polling: 'error' });
  });

  const isPaymentFulfilled = useSelector(actorRef, (state) => {
    return state.matches({ polling: 'fulfilled' });
  });

  // ─── Return ────────────────────────────────────────────────────────────────

  return {
    orderId: context.orderId,
    paymentState: context.paymentState,
    startPolling,
    createPayment,
    clearPaymentState,
    failPayment,
    requestPaymentCancelation,
    resetPolling,
    isPollingPayment,
    isPaymentPending,
    isPaymentDelayed,
    isCancelingPayment,
    isPaymentFailed,
    isPaymentFulfilled,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UsePaymentStatePollingParams = {
  paymentStatePollingInterval: number;
  paymentStateExpirationTolerance: number;
  paymentStateDelayedTolerance: number;

  fetchPaymentState: typeof PaymentStatePollingActors.fetchPaymentState;
  cancelPayment: typeof PaymentStatePollingActors.cancelPayment;
  clearPendingPayment: typeof PaymentStatePollingActions.clearPendingPayment;
  incrementPurchaseAttempt: typeof PaymentStatePollingActions.incrementPurchaseAttempt;
  onPaymentStateError: typeof PaymentStatePollingActions.onPaymentStateError;

  onPaymentCanceled?: typeof PaymentStatePollingActions.onPaymentCanceled;
  onPaymentFulfilled?: typeof PaymentStatePollingActions.onPaymentFulfilled;
};
